// SecurityHoldersModal.jsx
import { useState, useEffect, useCallback } from "react";
import { Modal, Spinner, Dropdown, Alert } from 'react-bootstrap';
import { 
  FaDownload, FaTimes, FaBuilding, FaUser, FaChartBar, 
  FaFilePdf, FaFileExcel, FaFilter 
} from "react-icons/fa";
import { getSecurityHolders } from "../../../service/api/SecurityService";
import { toast } from "sonner";
import useSelectedDomain from "../../../service/Hooks/useSelectedDomain";
import '../style/SecurityHoldersModal.css';
import { reportsService } from "../../../service/api/ReportsService";
import SecurityHoldersPagination from './Paginations/SecurityHoldersPagination';
import useDebounce from "../../../service/utils/useDebounce";

export default function SecurityHoldersModal({ show, onHide, securityId, securityName }) {
  const [holdersData, setHoldersData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [searchParams, setSearchParams] = useState({});
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 5,
    totalCount: 0,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false
  });
  
  const selectedDomain = useSelectedDomain();
  const debouncedSearchParams = useDebounce(searchParams, 1000); // Réduit à 500ms pour temps réel

  // Charger les données initiales
  useEffect(() => {
    if (show && securityId && selectedDomain?.id) {
      fetchHoldersData(1, pagination.pageSize, {});
    } else {
      // Réinitialiser les données quand le modal se ferme
      resetData();
    }
  }, [show, securityId, selectedDomain]);

  // Effectuer la recherche avec debounce (temps réel)
  useEffect(() => {
    if (show && securityId && selectedDomain?.id) {
      // Réinitialiser à la première page lors d'une nouvelle recherche
      fetchHoldersData(1, pagination.pageSize, debouncedSearchParams);
    }
  }, [debouncedSearchParams]);

  const fetchHoldersData = useCallback(async (pageNumber, pageSize, search = {}) => {
    if (!securityId || !selectedDomain?.id) return;
    
    setLoading(true);
    try {
      const data = await getSecurityHolders(
        selectedDomain.id, 
        securityId, 
        pageNumber, 
        pageSize, 
        search
      );

      
      if (data && data.items && data.items.length > 0) {
        // Prend le premier élément du tableau (puisqu'on a un seul securityId)
        const securityData = data.items[0];
        
        // S'assurer que holders existe
        if (securityData.holders && Array.isArray(securityData.holders)) {
          setHoldersData(securityData);
        } else {
          // Si holders n'existe pas, initialiser un tableau vide
          setHoldersData({
            ...securityData,
            holders: []
          });
        }
        
        // Mettre à jour la pagination
        setPagination({
          pageNumber: data.pageNumber,
          pageSize: data.pageSize,
          totalCount: data.totalCount,
          totalPages: data.totalPages,
          hasPreviousPage: data.hasPreviousPage,
          hasNextPage: data.hasNextPage
        });
      } else {
        // Si pas de données mais une réponse existe
        if (data) {
          setHoldersData({
            ...data,
            holders: []
          });
          setPagination({
            pageNumber: data.pageNumber || 1,
            pageSize: data.pageSize || pagination.pageSize,
            totalCount: data.totalCount || 0,
            totalPages: data.totalPages || 0,
            hasPreviousPage: data.hasPreviousPage || false,
            hasNextPage: data.hasNextPage || false
          });
        } else {
          setHoldersData(null);
          setPagination(prev => ({
            ...prev,
            totalCount: 0,
            totalPages: 0,
            hasPreviousPage: false,
            hasNextPage: false
          }));
        }
        // toast.info("Aucun détenteur trouvé pour ce titre");
      }
    } catch (error) {
      console.error("Erreur lors du chargement des détenteurs:", error);
      toast.error(error.message || "Erreur lors du chargement des détenteurs");
    } finally {
      setLoading(false);
    }
  }, [securityId, selectedDomain]);


  const handlePageChange = (newPage) => {
    if (newPage !== pagination.pageNumber) {
      fetchHoldersData(newPage, pagination.pageSize, searchParams);
    }
  };

  const handleDownloadReport = async (format = 'Pdf') => {
    if (!securityId || !selectedDomain?.id) return;
    
    setDownloading(true);
    try {
      // Inclure les paramètres de recherche dans le rapport
      const reportParams = {
        domainId: selectedDomain.id,
        securityId: securityId,
        format: format,
        securityName: securityName,
        ...searchParams
      };
      
      await reportsService.generateHoldersReport(reportParams);
      
      const formatLabel = format === 'Pdf' ? 'PDF' : 'Excel';
      toast.success(`Rapport ${formatLabel} téléchargé avec succès !`);
    } catch (error) {
      console.error("Erreur lors du téléchargement du rapport:", error);
      toast.error(error.message || "Erreur lors du téléchargement du rapport");
    } finally {
      setDownloading(false);
    }
  };

  const calculateTotalQuantity = () => {
    if (!holdersData?.holders) return 0;
    return holdersData.holders.reduce((total, holder) => total + holder.quantity, 0);
  };

  const calculateTotalHolders = () => {
    // Retourne le nombre total de détenteurs (toutes pages)
    return pagination.totalCount;
  };

  const resetData = () => {
    setHoldersData(null);
    setSearchParams({});
    setPagination({
      pageNumber: 1,
      pageSize: 5,
      totalCount: 0,
      totalPages: 0,
      hasPreviousPage: false,
      hasNextPage: false
    });
  };

  const handleClose = () => {
    resetData();
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered className="security-holders-modal">
      <Modal.Header closeButton className="modal-header-custom">
        <div className="d-flex align-items-center justify-content-between w-100">
          <div className="d-flex align-items-center">
            <FaChartBar className="me-2 text-primary" size={20} />
            <Modal.Title className="modal-title-custom text-white">
              Détenteurs du Titre
              {securityName && <span className="security-name">: {securityName}</span>}
            </Modal.Title>
          </div>
          
        </div>
      </Modal.Header>

      <Modal.Body className="p-0">
        {loading && !holdersData ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Chargement des détenteurs...</p>
          </div>
        ) : holdersData ? (
          <div className="holders-container">
            {/* En-tête avec informations du domaine */}
            <div className="domain-info-section p-4 border-bottom">
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <FaBuilding className="text-secondary me-2" />
                    <div>
                      <h6 className="mb-1 fw-bold">{holdersData.domainName}</h6>
                      <small className="text-muted">{holdersData.domainNumber}</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="text-md-end">
                    <p className="mb-1">
                      <strong>Email:</strong> {holdersData.domainEmail}
                    </p>
                    <p className="mb-0">
                      <strong>Ville:</strong> {holdersData.domainVille}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Résumé et filtres actifs */}
            <div className="summary-section p-3 bg-light">
              <div className="row align-items-center">
                <div className="col-md-8">
                  <div className="row text-center">
                    <div className="col-md-3">
                      <div className="summary-item">
                        <h4 className="text-primary mb-1">
                          {pagination.totalCount.toLocaleString('fr-FR')}
                        </h4>
                        <small className="text-muted">Total Détenteurs</small>
                        <div className="small text-muted mt-1">
                          (Page: {holdersData.holders?.length || 0})
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="summary-item">
                        <h4 className="text-success mb-1">
                          {calculateTotalQuantity().toLocaleString('fr-FR')}
                        </h4>
                        <small className="text-muted">Total Titres</small>
                        <div className="small text-muted mt-1">
                          (Cette page)
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="summary-item">
                        <h4 className="text-info mb-1">{holdersData.securityName}</h4>
                        <small className="text-muted">Titre</small>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="summary-item">
                        <h4 className="text-warning mb-1">{pagination.totalPages}</h4>
                        <small className="text-muted">Pages Total</small>
                        <div className="small text-muted mt-1">
                          (Page actuelle: {pagination.pageNumber})
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-4">
                  <div className="text-md-end">
                    {searchParams.accountName && (
                      <Alert variant="info" className="mb-0 py-2 d-inline-flex align-items-center">
                        <FaFilter className="me-2" size={12} />
                        <span className="small">
                          Filtre actif: <strong>{searchParams.accountName}</strong>
                        </span>
                      </Alert>
                    )}
                  </div>
                </div>
              </div>
            </div>
{/* 
            
            <div className="search-section p-3 border-bottom">
              <SecurityHoldersSearch
                onSearch={handleSearch}
                onReset={handleResetSearch}
                placeholder="Rechercher un détenteur (recherche en temps réel)..."
                loading={loading}
                showResetButton={true}
                className="main-search"
              />
            </div> */}

            {/* Liste des détenteurs */}
            <div className="holders-list-section">
              {loading && holdersData ? (
                <div className="text-center py-3 border-bottom">
                  <Spinner animation="border" size="sm" variant="primary" />
                  <span className="ms-2 small text-muted">Mise à jour des détenteurs...</span>
                </div>
              ) : null}
              
              <div className="table-responsive">
                <table className="table table-hover mb-0 table-striped">
                  <thead className="table-light">
                    <tr>
                      <th>Détenteur</th>
                      <th className="text-center">Quantité</th>
                      <th className="text-center">Pourcentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {holdersData.holders?.map((holder, index) => {
                      const total = calculateTotalQuantity();
                      const percentage = total > 0 ? ((holder.quantity / total) * 100).toFixed(1) : 0;
                      
                      return (
                        <tr key={holder.accountId || index} className="holder-row">
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="holder-avatar bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3">
                                <FaUser size={12} />
                              </div>
                              <div>
                                <h6 className="mb-0 fw-semibold">{holder.holderName}</h6>
                              </div>
                            </div>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-primary badge-transparent rounded-pill px-3 py-2">
                              {holder.quantity.toLocaleString('fr-FR')}
                            </span>
                          </td>
                          <td className="text-center">
                            <div className="progress-wrapper" style={{ maxWidth: '100px', margin: '0 auto' }}>
                              <div className="d-flex justify-content-between small mb-1">
                                <span>{percentage}%</span>
                              </div>
                              <div className="progress" style={{ height: '6px' }}>
                                <div 
                                  className="progress-bar" 
                                  role="progressbar" 
                                  style={{ width: `${percentage}%` }}
                                  aria-valuenow={percentage} 
                                  aria-valuemin="0" 
                                  aria-valuemax="100"
                                ></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    
                    {(!holdersData.holders || holdersData.holders.length === 0) && (
                      <tr>
                        <td colSpan="3" className="text-center py-4">
                          <div className="text-muted">
                            {/* <FaTimes className="mb-2" size={24} /> */}
                            <p className="mb-0">
                              {searchParams.accountName 
                                ? `Aucun détenteur trouvé pour "${searchParams.accountName}"`
                                : "Aucun détenteur disponible pour ce titre"
                              }
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination avec statistiques */}
              {pagination.totalPages > 0 && (
                <div className="border-top p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="small text-muted">
                      <span>Affichage de <strong>{holdersData.holders?.length || 0}</strong> détenteurs sur <strong>{pagination.totalCount.toLocaleString('fr-FR')}</strong> total</span>
                    </div>
                    <div className="small text-muted">
                      Page <strong>{pagination.pageNumber}</strong> sur <strong>{pagination.totalPages}</strong>
                    </div>
                  </div>
                  
                  <SecurityHoldersPagination
                    currentPage={pagination.pageNumber}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalCount}
                    itemsPerPage={pagination.pageSize}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-5">
            <FaTimes className="text-muted mb-3" size={40} />
            <p className="text-muted">Aucune donnée de détenteurs disponible</p>
            {!loading && (
              <button 
                className="btn btn-outline-primary mt-2"
                onClick={() => fetchHoldersData(1, pagination.pageSize, {})}
              >
                Réessayer
              </button>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="modal-footer-custom">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="text-muted small">
            {holdersData ? (
              pagination.totalCount > 0 ? (
                <>Affichage de <strong>{holdersData.holders?.length || 0}</strong> détenteurs sur <strong>{pagination.totalCount}</strong> total</>
              ) : (
                <>Aucun détenteur trouvé pour ce titre</>
              )
            ) : (
              <>Chargement des données...</>
            )}
          </div>
          
          <div className="d-flex gap-2">
            <button 
              className="btn btn-outline-secondary" 
              onClick={handleClose}
              disabled={downloading}
            >
              Fermer
            </button>
            
            {/* Bouton de téléchargement conditionnel avec feedback clair */}
            {pagination.totalCount > 0 ? (
              <Dropdown>
                <Dropdown.Toggle 
                  variant="primary" 
                  disabled={downloading || loading}
                  className="d-flex align-items-center"
                >
                  {downloading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                      Téléchargement...
                    </>
                  ) : loading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                      Chargement...
                    </>
                  ) : (
                    <>
                      <FaDownload className="me-2" />
                      Télécharger le Rapport ({pagination.totalCount})
                    </>
                  )}
                </Dropdown.Toggle>

                <Dropdown.Menu className="shadow border-0">
                  <Dropdown.Item 
                    onClick={() => handleDownloadReport('Pdf')}
                    className="d-flex align-items-center py-2"
                  >
                    <FaFilePdf className="me-2 text-danger" size={16} />
                    <div>
                      <div className="fw-semibold">Format PDF</div>
                      <small className="text-muted">Optimisé pour l'impression</small>
                    </div>
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item 
                    onClick={() => handleDownloadReport('Excel')}
                    className="d-flex align-items-center py-2"
                  >
                    <FaFileExcel className="me-2 text-success" size={16} />
                    <div>
                      <div className="fw-semibold">Format Excel</div>
                      <small className="text-muted">Idéal pour l'analyse</small>
                    </div>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              // Bouton désactivé quand pas de données
              <button 
                className="btn btn-light text-dark" 
                disabled
                title="Aucun détenteur à télécharger"
              >
                <FaDownload className="me-2" />
                Télécharger le Rapport
              </button>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}